// Runtime theme resolution. The theme used to be fixed at build time (`NEXT_PUBLIC_THEME`, inlined by
// Next), which meant one build per theme; `dugout` is now resolvable per request from a `?theme=`
// param or the request hostname, with the build theme as the fallback.
//
// Deliberately PURE — no `next/*` imports, no I/O — so it is safe to import from a client component
// (ToolContext needs the `Theme` type) and can be unit-tested without a request.
//
// SCOPE: only the dugout switch is genuinely runtime. The frf/fmf shirt-asset filenames in
// config/itemTypes.ts and the locale set in i18n/request.ts are still baked per build, so
// `?theme=frf` on a knvb build yields frf CSS with knvb shirts. See README.

export const THEMES = [
  'knvb',
  'frf',
  'fmf',
  'default',
  'dugout',
  'sportwide',
  'worldcoaches',
  'fc-gymles',
] as const;

export type Theme = (typeof THEMES)[number];

/** Request header the proxy uses to hand the resolved theme to the server components. */
export const THEME_HEADER = 'x-rinus-theme';

/** Last resort when neither the request nor the build names a valid theme. */
export const FALLBACK_THEME: Theme = 'default';

const THEME_SET: ReadonlySet<string> = new Set(THEMES);

/**
 * The ONLY gate between a user-supplied string and the `<link href={`/css/themes/${theme}/…`}>` in
 * app/layout.tsx, so it is a security boundary: `?theme=../../etc/passwd`, `dugout%00` and
 * `//evil.example` must all fail here and fall through to the build theme. Deliberately an exact
 * match (no trimming or lowercasing) — the callers are machine-generated URLs, so there is no
 * ergonomic loss and the reasoning stays trivial.
 */
export const isTheme = (value: unknown): value is Theme =>
  typeof value === 'string' && THEME_SET.has(value);

/**
 * Hostname → theme overrides, hardcoded so a new host is a reviewed code change rather than a config
 * edit. A `Map` (not a `Record`) so a hostile hostname like `__proto__` cannot reach
 * `Object.prototype` on lookup.
 */
const HOST_THEMES: ReadonlyMap<string, Theme> = new Map<string, Theme>([
  ['drawing-dugout.knvb.nl', 'dugout'],
  ['acc-drawing-dugout.knvb.nl', 'dugout'],
  // Local dev + the e2e host-map project. `*.localhost` resolves to loopback in Chromium and macOS,
  // so it needs no /etc/hosts entry.
  ['dugout.localhost', 'dugout'],
]);

/**
 * A raw `Host` / `X-Forwarded-Host` value → bare lowercase hostname. Handles the three shapes those
 * headers actually arrive in: a proxy-appended comma list (`a.example, b.example` — the first entry
 * is the original client host), a `:port` suffix, and a bracketed IPv6 literal.
 */
export const normaliseHost = (
  raw: string | null | undefined,
): string | null => {
  if (!raw) return null;
  const first = (raw.split(',', 1)[0] ?? '').trim();
  if (!first) return null;
  const withoutPort = first.startsWith('[')
    ? first.slice(0, first.indexOf(']') + 1) // an IPv6 literal keeps its brackets
    : (first.split(':', 1)[0] ?? '');
  return withoutPort.toLowerCase() || null;
};

export interface ResolveThemeInput {
  /** Value of THEME_HEADER — the proxy's already-resolved decision. */
  headerTheme?: string | null;
  /** Raw `theme` query value. Only the proxy reads this; never the client. */
  queryTheme?: string | null;
  /** Raw `Host` / `X-Forwarded-Host` value. */
  host?: string | null;
  /** `process.env.NEXT_PUBLIC_THEME` — what this bundle was built as. */
  buildTheme?: string | null;
  fallback?: Theme;
}

/**
 * Resolve the theme for one request: header → `?theme=` → hostname map → build theme → fallback.
 *
 * The header wins so the proxy resolves once per request and app/layout.tsx can re-run this to
 * RE-VALIDATE that header rather than trusting it — an inbound `x-rinus-theme: ../..` can never reach
 * the stylesheet href, even if the proxy matcher stops covering a route.
 */
export const resolveTheme = ({
  headerTheme,
  queryTheme,
  host,
  buildTheme,
  fallback = FALLBACK_THEME,
}: ResolveThemeInput): Theme => {
  if (isTheme(headerTheme)) return headerTheme;
  if (isTheme(queryTheme)) return queryTheme;

  const hostname = normaliseHost(host);
  const mapped = hostname === null ? undefined : HOST_THEMES.get(hostname);
  if (mapped) return mapped;

  if (isTheme(buildTheme)) return buildTheme;
  return fallback;
};
